import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '@/app/hooks';
import { useScrollBehavior } from '@/hooks/useScrollBehavior';
import { openUploadModal } from '@/features/ui/uiSlice';
import { setQuery, executeSearch, clearSearch, toggleModeTag, clearModeTags, setAssetType } from '@/features/search/searchSlice';
import type { AssetTypeFilter } from '@/types';
import { logout } from '@/features/auth/authSlice';
import SearchBar from '@/components/ui/SearchBar/SearchBar';
import ThemeToggle from '@/components/ui/ThemeToggle/ThemeToggle';
import { trackEvent } from '@/services/analyticsService';
import { ANALYTICS_EVENTS } from '@/config/analyticsEvents';
import styles from './Navbar.module.css';

export default function Navbar({ showSearch = true }: { showSearch?: boolean }) {
  const navScrolled = useAppSelector((s) => s.ui.navScrolled);
  const searchQuery = useAppSelector((s) => s.search.query);
  const uploadInProgress = useAppSelector((s) => s.ui.uploadInProgress);
  const authName = useAppSelector((s) => s.auth.name);
  const availableTags = useAppSelector((s) => s.search.availableTags);
  const activeModeTagIds = useAppSelector((s) => s.search.activeModeTagIds);
  const activeAssetType = useAppSelector((s) => s.search.activeAssetType);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const avatarInitials = (() => {
    if (!authName) return '?';
    const parts = authName.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  })();

  const [avatarOpen, setAvatarOpen] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const [assetFilterOpen, setAssetFilterOpen] = useState(false);
  const avatarRef = useRef<HTMLDivElement>(null);
  const assetFilterRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useScrollBehavior();

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (avatarRef.current && !avatarRef.current.contains(e.target as Node)) {
        setAvatarOpen(false);
      }
    };
    if (avatarOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [avatarOpen]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (assetFilterRef.current && !assetFilterRef.current.contains(e.target as Node)) {
        setAssetFilterOpen(false);
      }
    };
    if (assetFilterOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [assetFilterOpen]);

  const handleLogout = () => {
    trackEvent(ANALYTICS_EVENTS.LOGOUT);
    dispatch(logout());
    setAvatarOpen(false);
    navigate('/signin', { replace: true });
  };

  const handleSearchChange = (value: string) => {
    dispatch(setQuery(value));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.length === 0) {
      dispatch(clearSearch());
      return;
    }
    if (value.length >= 2) {
      // Mode is derived inside executeSearch from live Redux state, so no
      // stale-closure problem even with a 500 ms debounce.
      debounceRef.current = setTimeout(() => {
        trackEvent(ANALYTICS_EVENTS.SEARCH_SUBMIT, { query: value });
        dispatch(executeSearch({ query: value, limit: 15 }));
      }, 500);
    }
  };

  // Asset-type filter attached to the search bar — maps to the search API
  // payload `asset_type` (both | video | image). Shares Redux `activeAssetType`
  // with the in-results filter chips, so the two stay in sync.
  const ASSET_TYPE_OPTIONS: { value: AssetTypeFilter; label: string }[] = [
    { value: 'both', label: 'All media' },
    { value: 'video', label: 'Videos' },
    { value: 'image', label: 'Images' },
  ];
  const currentAssetType: AssetTypeFilter = activeAssetType ?? 'both';
  const currentAssetTypeLabel = ASSET_TYPE_OPTIONS.find((o) => o.value === currentAssetType)?.label ?? 'All media';

  const handleAssetTypeSelect = (value: AssetTypeFilter) => {
    setAssetFilterOpen(false);
    trackEvent(ANALYTICS_EVENTS.SEARCH_ASSET_TYPE_FILTER, { assetType: value });
    // Store null for the "both" default so it matches the in-results chip state.
    dispatch(setAssetType(value === 'both' ? null : value));
    if (searchQuery.length >= 2) {
      dispatch(executeSearch({ query: searchQuery, limit: 15 }));
    }
  };

  const handleModeTagToggle = (tagId: string) => {
    // toggleModeTag is synchronous — Redux state is updated before executeSearch
    // reads activeModeTagIds, so mode is always derived from the correct set.
    trackEvent(ANALYTICS_EVENTS.SEARCH_MODE_TAG_TOGGLE, { tagId });
    dispatch(toggleModeTag(tagId));
    if (searchQuery.length >= 2) {
      dispatch(executeSearch({ query: searchQuery, limit: 15 }));
    }
  };

  return (
    <nav className={`${styles.nav} ${navScrolled ? styles.navScrolled : ''}`}>
      {/* Search bar + mode chips — hidden on pages that don't search (e.g. the
          collection "See all" page), so the nav shows only the right-side controls. */}
      {showSearch ? (
      <div className={styles.searchWrap}>
        {/* Asset-type filter — scopes the search to videos, images, or both */}
        <div className={styles.assetFilter} ref={assetFilterRef}>
          <button
            type="button"
            className={`${styles.assetFilterBtn} ${currentAssetType !== 'both' ? styles.assetFilterBtnActive : ''}`}
            onClick={() => setAssetFilterOpen((v) => !v)}
            aria-haspopup="listbox"
            aria-expanded={assetFilterOpen}
            aria-label={`Filter search by media type: ${currentAssetTypeLabel}`}
          >
            {currentAssetType === 'image' ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="9" cy="9" r="2" />
                <path d="M21 15l-5-5L5 21" />
              </svg>
            ) : currentAssetType === 'video' ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="2" y="5" width="14" height="14" rx="2" />
                <path d="M22 8l-6 4 6 4V8z" />
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M3 9h18M9 21V9" />
              </svg>
            )}
            <span className={styles.assetFilterLabel}>{currentAssetTypeLabel}</span>
            <svg width="9" height="9" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M2 4l4 4 4-4" />
            </svg>
          </button>
          {assetFilterOpen && (
            <div className={styles.assetFilterMenu} role="listbox" aria-label="Media type">
              {ASSET_TYPE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  role="option"
                  aria-selected={currentAssetType === opt.value}
                  className={`${styles.assetFilterOption} ${currentAssetType === opt.value ? styles.assetFilterOptionActive : ''}`}
                  onClick={() => handleAssetTypeSelect(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <SearchBar
          placeholder="Search videos, projects, people..."
          value={searchQuery}
          onChange={handleSearchChange}
          onFocus={() => setInputFocused(true)}
          onBlur={() => setInputFocused(false)}
        />

        {/* Active mode chips — visible whenever filters are set */}
        {activeModeTagIds.length > 0 && (
          <div className={styles.activeModeRow}>
            {activeModeTagIds.map((id) => {
              const tag = availableTags.find((t) => t.id === id);
              if (!tag) return null;
              return (
                <button
                  key={id}
                  className={styles.activeModeChip}
                  onMouseDown={(e) => { e.preventDefault(); handleModeTagToggle(id); }}
                  aria-label={`Remove ${tag.label} filter`}
                  aria-pressed
                >
                  {tag.label}
                  <svg width="9" height="9" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
                    <path d="M1 1l10 10M11 1L1 11" />
                  </svg>
                </button>
              );
            })}
            <button
              className={styles.clearModesBtn}
              onMouseDown={(e) => {
                e.preventDefault();
                dispatch(clearModeTags());
                if (searchQuery.length >= 2) dispatch(executeSearch({ query: searchQuery, limit: 15 }));
              }}
            >
              Clear
            </button>
          </div>
        )}

        {/* Mode suggestion dropdown — shown when user is typing */}
        {inputFocused && searchQuery.length >= 1 && availableTags.length > 0 && (
          <div className={styles.modeDropdown} role="group" aria-label="Search mode suggestions">
            <span className={styles.modeDropdownLabel}>Search by</span>
            {availableTags.map((tag) => (
              <button
                key={tag.id}
                className={`${styles.modeChip} ${activeModeTagIds.includes(tag.id) ? styles.modeChipActive : ''}`}
                onMouseDown={(e) => { e.preventDefault(); handleModeTagToggle(tag.id); }}
                aria-pressed={activeModeTagIds.includes(tag.id)}
                title={tag.description}
              >
                {tag.label}
              </button>
            ))}
          </div>
        )}
      </div>
      ) : null}

      {/* Right side controls */}
      <div className={styles.navRight}>
        {/* + Upload (video, image, or any other supported asset type) */}
        <button
          className={`${styles.newVideoBtn} ${uploadInProgress ? styles.newVideoBtnBusy : ''}`}
          onClick={() => { if (!uploadInProgress) { trackEvent(ANALYTICS_EVENTS.UPLOAD_MODAL_OPEN); dispatch(openUploadModal()); } }}
          disabled={uploadInProgress}
          aria-label={uploadInProgress ? 'Upload in progress' : 'Upload'}
        >
          {!uploadInProgress && (
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          )}
          {uploadInProgress ? 'Uploading…' : 'Upload'}
        </button>



        {/* Theme toggle */}
        <div className={styles.themeWrap}>
          <ThemeToggle />
        </div>

        {/* Avatar with popover */}
        <div
          className={`${styles.avatar} ${avatarOpen ? styles.avatarActive : ''}`}
          ref={avatarRef}
          role="button"
          tabIndex={0}
          aria-label={authName ? `User profile: ${authName}` : 'User profile'}
          aria-expanded={avatarOpen}
          data-tooltip={authName || undefined}
          onClick={() => setAvatarOpen((v) => !v)}
          onKeyDown={(e) => e.key === 'Enter' && setAvatarOpen((v) => !v)}
        >
          {avatarInitials}
          {avatarOpen && (
            <div className={styles.avatarPopover} role="menu">
              <button className={styles.avatarPopoverItem} role="menuitem" onClick={handleLogout}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
